import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  Injector,
  Input,
  OnInit,
} from '@angular/core';
import { FbMatrixWidget } from '../../../models/form-widget-matrix.model';
import { WidgetBase } from '../../../models/widget-base';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TokenToDivPipe } from '../../../helpers/token-to-div.pipe';
import { MatRadioModule } from '@angular/material/radio';
import { ScribeSuggestionIndicatorComponent } from '../shared/scribe-suggestion-indicator/scribe-suggestion-indicator.component';
import { AmendmentFieldIndicatorComponent } from '../shared/amendment-field-indicator/amendment-field-indicator.component';

@Component({
  selector: 'app-fb-matrix',
  templateUrl: './fb-matrix.component.html',
  styleUrls: ['./fb-matrix.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    TokenToDivPipe,
    MatRadioModule,
    ScribeSuggestionIndicatorComponent,
    AmendmentFieldIndicatorComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FbMatrixComponent extends WidgetBase implements OnInit, AfterViewInit {
  /**
   * True while a patient's own plan is being customized. The widget then keeps the saved answers
   * exactly as stored: the whole payload is submitted back, so it must not be normalized.
   */
  public get isInstanceDesign(): boolean {
    return !!this.isDesign && !!this.entity;
  }

  @Input()
  public override set payload(value) {
    this._payload = value;

    if (this.isInstanceDesign) {
      return;
    }

    const rows = this._payload?.properties?.rows?.value;
    if (!rows) return;

    for (const row of rows) {
      const selected = row.properties.selected;

      // --- Handle legacy format { value: number } ---
      if (selected && typeof selected === 'object' && 'value' in selected) {
        // Save pointer to legacy object so it can be updated later
        row.properties._selectedLegacy = selected;

        // Convert legacy format to primitive
        row.properties.selected = selected.value ?? -1;
      } else {
        // --- Handle new format (primitive) ---
        if (selected == null || selected === undefined) {
          row.properties.selected = -1; // nothing selected
        }
      }
    }
  }

  public override get payload() {
    return this._payload;
  }

  constructor(private injector: Injector) {
    super(injector);
  }

  ngOnInit() {}

  ngAfterViewInit() {
    this.ready.next(true);
  }

  // --- Unified selection handler for both old and new formats ---
  onRowSelectionChange(row: any, value: number) {
    if (this.isInstanceDesign) {
      return;
    }

    this.clearScribeSuggestionMarker(this.payload);

    // Update new format (primitive)
    row.properties.selected = value;

    // Update legacy { value: number } if exists
    if (row.properties._selectedLegacy) {
      row.properties._selectedLegacy.value = value;
    }
  }

  override isValid() {
    if (!this.payload) return true;
    if (!this.payload.properties.required?.value) return true;

    // Validation works for both legacy and new format after normalization
    return this.payload.properties.rows.value.every(
      (row) => row.properties.selected != null && row.properties.selected !== -1,
    );
  }
}
